import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createPathologistReport,
  updatePathologistReport,
  fetchPathologistReportById,
} from "../../store/slices/pathologistResultSlice";
import { fetchTestOrders } from "../../store/slices/testOrderSlice";
import uploadService from "../../services/uploadService";
import toast from "../../utils/toast";

const PathologistReportForm = ({ report = null, onClose }) => {
  const dispatch = useDispatch();
  const { token } = useSelector((s) => s.auth || {});
  const { loading } = useSelector((s) => s.pathologistReports || {});
  const { user } = useSelector((state) => state.auth || {});
  const { testOrders } = useSelector((s) => s.testOrders || {});

  const orders = useMemo(
    () => (Array.isArray(testOrders) ? testOrders : testOrders?.data || []),
    [testOrders]
  );
  const [form, setForm] = useState({
    test_order_id: report?.test_order_id || "",
    pathologist_id: user?.id || "",
    images: report?.images ? JSON.stringify(report.images) : "[]",
    overall_comments: report?.overall_comments || "",
    status: report?.status || "draft",
    is_verified: report?.is_verified ? true : false,
    verified_by: report?.verified_by || null,
  });

  const isLocked = Boolean(report?.is_verified) || String(report?.status || "").toLowerCase() === "verified";

  const [uploading, setUploading] = useState(false);

  // Key-value pairs state for report data
  const [keyValuePairs, setKeyValuePairs] = useState([{ key: "", value: "" }]);

  useEffect(() => {
    if (!orders || orders.length === 0) {
      dispatch(fetchTestOrders());
    }
    if (report?.id) dispatch(fetchPathologistReportById(report.id));
  }, [report, dispatch, orders]);

  // Initialize key-value pairs from existing report data
  useEffect(() => {
    if (report?.report_data) {
      try {
        const reportData =
          typeof report.report_data === "string"
            ? JSON.parse(report.report_data)
            : report.report_data;

        const pairs = Object.entries(reportData).map(([key, value]) => ({
          key,
          value: String(value),
        }));

        setKeyValuePairs(pairs.length > 0 ? pairs : [{ key: "", value: "" }]);
      } catch (error) {
        console.error("Error parsing report data:", error);
        setKeyValuePairs([{ key: "", value: "" }]);
      }
    }
  }, [report]);

  // Convert key-value pairs to JSON
  const convertToJSON = (pairs) => {
    const jsonData = {};
    pairs.forEach((pair) => {
      if (pair.key.trim() !== "") {
        jsonData[pair.key.trim()] = pair.value;
      }
    });
    return jsonData;
  };

  const handleKeyValueChange = (index, field, value) => {
    const updatedPairs = [...keyValuePairs];
    updatedPairs[index][field] = value;
    setKeyValuePairs(updatedPairs);
  };

  const addKeyValuePair = () => {
    setKeyValuePairs([...keyValuePairs, { key: "", value: "" }]);
  };

  const removeKeyValuePair = (index) => {
    if (keyValuePairs.length > 1) {
      const updatedPairs = keyValuePairs.filter((_, i) => i !== index);
      setKeyValuePairs(updatedPairs);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const reportData = convertToJSON(keyValuePairs);

      const payload = {
        test_order_id: form.test_order_id,
        pathologist_id: user?.id || form.pathologist_id,
        report_data: reportData,
        images: form.images ? JSON.parse(form.images) : null,
        overall_comments: form.overall_comments || null,
        status: form.status,
        is_verified: form.is_verified ? 1 : 0,
        verified_by: form.verified_by || null,
      };

      if (report?.id) {
        await dispatch(
          updatePathologistReport({ id: report.id, data: payload })
        ).unwrap();
      } else {
        await dispatch(createPathologistReport(payload)).unwrap();
      }
      onClose?.();
    } catch (err) {
      console.error("Save failed", err);
      alert(err || "Failed to save report");
    }
  };

  const inputBase =
    "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-transparent focus:ring-2 focus:ring-[#235F72] disabled:bg-gray-50 disabled:text-gray-500 disabled:shadow-none disabled:cursor-not-allowed";
  const labelBase = "block text-sm font-semibold text-gray-800";
  const helpBase = "mt-1 text-xs text-gray-500";

  const primaryBtn =
    "inline-flex items-center justify-center gap-2 rounded-xl bg-[#235F72] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a4a5a] focus:outline-none focus:ring-2 focus:ring-[#235F72] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const secondaryBtn =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#235F72] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const dangerBtn =
    "inline-flex items-center justify-center rounded-xl px-2.5 py-2 text-red-700 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed";
  const successBtn =
    "inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";

  const parsedImages = useMemo(() => {
    try {
      const v = form.images ? JSON.parse(form.images) : [];
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  }, [form.images]);

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const sigResp = await uploadService.cloudinarySignature(
        { folder: "lis/pathologist" },
        token
      );
      const sig = sigResp?.data?.data ?? sigResp?.data ?? sigResp;
      const { cloudName, apiKey, timestamp, signature, folder } = sig || {};

      if (!cloudName || !apiKey || !timestamp || !signature) {
        throw new Error("Cloudinary signature response is incomplete");
      }

      const url = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;
      const body = new FormData();
      body.append("file", file);
      body.append("api_key", apiKey);
      body.append("timestamp", String(timestamp));
      body.append("signature", signature);
      if (folder) body.append("folder", folder);

      const resp = await fetch(url, { method: "POST", body });
      const json = await resp.json();
      if (!resp.ok) {
        const message = json?.error?.message || "Cloudinary upload failed";
        throw new Error(message);
      }

      if (!json?.secure_url) {
        throw new Error("Cloudinary did not return secure_url");
      }

      const next = [...parsedImages, String(json.secure_url)];
      setForm((prev) => ({ ...prev, images: JSON.stringify(next) }));
      toast.success("Image uploaded.");
    } finally {
      setUploading(false);
    }
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      // Upload all files in parallel
      const sigResp = await uploadService.cloudinarySignature(
        { folder: "lis/pathologist" },
        token
      );
      const sig = sigResp?.data?.data ?? sigResp?.data ?? sigResp;
      const { cloudName, apiKey, timestamp, signature, folder } = sig || {};
      if (!cloudName || !apiKey || !timestamp || !signature) {
        throw new Error("Cloudinary signature response is incomplete");
      }
      const url = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;
      const uploadPromises = files.map(async (file) => {
        const body = new FormData();
        body.append("file", file);
        body.append("api_key", apiKey);
        body.append("timestamp", String(timestamp));
        body.append("signature", signature);
        if (folder) body.append("folder", folder);
        const resp = await fetch(url, { method: "POST", body });
        const json = await resp.json();
        if (!resp.ok || !json?.secure_url) {
          throw new Error(json?.error?.message || "Cloudinary upload failed");
        }
        return String(json.secure_url);
      });
      const uploadedUrls = await Promise.all(uploadPromises);
      const next = [...parsedImages, ...uploadedUrls];
      setForm((prev) => ({ ...prev, images: JSON.stringify(next) }));
      toast.success(`${uploadedUrls.length} image(s) uploaded.`);
    } catch (err) {
      toast.error("Failed to upload one or more images");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImageAt = (idx) => {
    const next = parsedImages.filter((_, i) => i !== idx);
    setForm((prev) => ({ ...prev, images: JSON.stringify(next) }));
  };

  return (
    <form onSubmit={handleSubmit} className="h-[80vh] flex flex-col">
      <div className="mx-auto w-full max-w-5xl flex-1 min-h-0 p-4 sm:p-6">
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 h-full flex flex-col min-h-0">
          {/* Header (sticky) */}
          <div className="sticky top-0 z-10 border-b border-gray-200/70 bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#235F72]/10 px-3 py-1 text-xs font-semibold text-[#235F72] ring-1 ring-[#235F72]/20">
                  Pathology Report
                </div>
                <h2 className="mt-2 text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
                  {report ? "Edit Pathologist Report" : "New Pathologist Report"}
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  {report ? "Update the existing report" : "Create a new pathology report"}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button type="button" onClick={onClose} className={secondaryBtn}>
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    isLocked ||
                    loading ||
                    !form.test_order_id ||
                    keyValuePairs.every((pair) => !pair.key.trim())
                  }
                  className={primaryBtn}
                >
                  {loading ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {report ? "Update" : "Create"}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {isLocked && (
            <div className="border-b border-gray-200/70 bg-red-50 px-5 py-4 sm:px-6">
              <div className="flex flex-col gap-1">
                <div className="text-sm font-semibold text-red-800">Verified / Locked</div>
                <div className="text-xs text-red-700">
                  This report has been verified and is read-only. Contact an admin if changes are required.
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-6 sm:px-6 overscroll-contain">
            <div className="space-y-6">

            {/* Basic Information */}
            <section className="rounded-2xl bg-white ring-1 ring-gray-200/70">
              <div className="border-b border-gray-200/70 px-4 py-3 sm:px-5">
                <div className="text-sm font-semibold text-gray-900">Order</div>
                <div className="text-xs text-gray-500">Select the ordered test to report.</div>
              </div>

              <div className="p-4 sm:p-5">
                <label className={labelBase}>
                  Test Order <span className="text-red-600">*</span>
                </label>
                <p className={helpBase}>Pick the patient + test order you are reporting.</p>

                <select
                  required
                  value={form.test_order_id}
                  onChange={(e) => setForm({ ...form, test_order_id: e.target.value })}
                  className={`${inputBase} mt-2`}
                  disabled={isLocked}
                >
                  <option value="">-- Select test order --</option>
                  {orders.map((o) => {
                    const labelParts = [];
                    labelParts.push(`#${o.id}`);
                    if (o.patient_name) labelParts.push(o.patient_name);
                    if (o.test_name) labelParts.push(o.test_name);
                    if (o.card_number) labelParts.push(`(${o.card_number})`);
                    const label = labelParts.join(" • ");
                    return (
                      <option key={o.id} value={o.id}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
            </section>

            {/* Key-Value Pairs for Report Data */}
            <section className="rounded-2xl bg-white ring-1 ring-gray-200/70">
              <div className="flex flex-col gap-3 border-b border-gray-200/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    Results <span className="text-red-600">*</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Add findings as name + narrative result. Keep it concise and clinically clear.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={addKeyValuePair}
                  className={successBtn}
                  disabled={isLocked}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add row
                </button>
              </div>

              <div className="p-4 sm:p-5">
                <div className="space-y-3">
                  {keyValuePairs.map((pair, index) => (
                    <div
                      key={index}
                      className="rounded-2xl bg-white p-4 ring-1 ring-gray-200/70"
                    >
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-12 sm:items-start">
                        <div className="sm:col-span-4">
                          <label className="block text-xs font-semibold text-gray-700">Test Name</label>
                          <input
                            type="text"
                            value={pair.key}
                            onChange={(e) => handleKeyValueChange(index, "key", e.target.value)}
                            className={`${inputBase} mt-1`}
                            placeholder="e.g., Hemoglobin, WBC, Microscopic findings"
                            disabled={isLocked}
                          />
                        </div>

                        <div className="sm:col-span-7">
                          <label className="block text-xs font-semibold text-gray-700">Result Description</label>
                          <textarea
                            value={pair.value}
                            onChange={(e) => handleKeyValueChange(index, "value", e.target.value)}
                            rows={3}
                            className={`${inputBase} mt-1 resize-y`}
                            placeholder="Enter detailed findings and interpretation…"
                            disabled={isLocked}
                          />
                        </div>

                        <div className="sm:col-span-1 sm:flex sm:justify-end">
                          <button
                            type="button"
                            onClick={() => removeKeyValuePair(index)}
                            className={`${dangerBtn} mt-1`}
                            disabled={isLocked || keyValuePairs.length === 1}
                            title={
                              keyValuePairs.length === 1
                                ? "At least one test is required"
                                : "Remove test"
                            }
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            <span className="sr-only">Remove</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* JSON Preview (Collapsible) */}
                <details className="mt-4 overflow-hidden rounded-2xl ring-1 ring-gray-200/70">
                  <summary className="cursor-pointer select-none bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-100">
                    JSON preview (auto-generated)
                  </summary>
                  <div className="max-h-52 overflow-auto bg-gray-900 p-3 text-xs text-green-300">
                    <pre>{JSON.stringify(convertToJSON(keyValuePairs), null, 2)}</pre>
                  </div>
                </details>
              </div>
            </section>

            {/* Images Section */}
            <section className="rounded-2xl bg-white ring-1 ring-gray-200/70">
              <div className="border-b border-gray-200/70 px-4 py-3 sm:px-5">
                <div className="text-sm font-semibold text-gray-900">Images</div>
                <div className="text-xs text-gray-500">Multiple image upload (Cloudinary).</div>
              </div>

              <div className="p-4 sm:p-5">
                <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-5">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={onPickFiles}
                    className="sr-only"
                    id="image-upload"
                    disabled={isLocked || uploading}
                  />
                  <label
                    htmlFor="image-upload"
                    className={`block cursor-pointer rounded-xl bg-white px-4 py-4 text-center shadow-sm ring-1 ring-gray-200/70 transition hover:bg-gray-50 focus-within:ring-2 focus-within:ring-[#235F72] ${
                      isLocked ? "cursor-not-allowed opacity-60" : ""
                    }`}
                  >
                    <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-[#235F72]/10 text-[#235F72]">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="text-sm font-semibold text-gray-900">Upload images</div>
                    <div className="mt-1 text-xs text-gray-500">PNG/JPG up to 10MB each. You can select multiple images.</div>
                  </label>

                  {parsedImages.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs font-semibold text-gray-700">Preview</div>
                      <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {parsedImages.map((src, idx) => (
                          <div key={idx} className="overflow-hidden rounded-2xl ring-1 ring-gray-200/70">
                            <img
                              src={src}
                              alt={`upload-${idx}`}
                              className="h-28 w-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Comments + Status */}
            <section className="rounded-2xl bg-white ring-1 ring-gray-200/70">
              <div className="border-b border-gray-200/70 px-4 py-3 sm:px-5">
                <div className="text-sm font-semibold text-gray-900">Finalize</div>
                <div className="text-xs text-gray-500">Overall interpretation and report state.</div>
              </div>

              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:gap-6 sm:p-5">
                <div>
                  <label className={labelBase}>Overall Comments &amp; Interpretation</label>
                  <p className={helpBase}>Clinical correlation, impression, and key notes.</p>
                  <textarea
                    rows={5}
                    value={form.overall_comments}
                    onChange={(e) => setForm({ ...form, overall_comments: e.target.value })}
                    className={`${inputBase} mt-2 resize-y`}
                    placeholder="Add overall comments, clinical correlation, and final interpretation…"
                    disabled={isLocked}
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={labelBase}>Report Status</label>
                    <p className={helpBase}>Draft → Completed → Verified</p>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className={`${inputBase} mt-2`}
                      disabled={isLocked}
                    >
                      <option value="draft">Draft</option>
                      <option value="completed">Completed</option>
                      <option value="verified">Verified</option>
                    </select>
                  </div>

                  <div className="rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200/70">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="verified"
                        checked={form.is_verified}
                        onChange={(e) => setForm({ ...form, is_verified: e.target.checked })}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#235F72] focus:ring-[#235F72] disabled:cursor-not-allowed"
                        disabled={isLocked}
                      />
                      <div>
                        <label htmlFor="verified" className="text-sm font-semibold text-gray-800">
                          Mark as verified
                        </label>
                        <div className="text-xs text-gray-500">
                          Verified reports become locked and read-only.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer actions (mobile-friendly) */}
            <div className="flex flex-col-reverse gap-2 border-t border-gray-200/70 pt-5 sm:flex-row sm:justify-end">
              <button type="button" onClick={onClose} className={secondaryBtn}>
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  isLocked ||
                  loading ||
                  !form.test_order_id ||
                  keyValuePairs.every((pair) => !pair.key.trim())
                }
                className={primaryBtn}
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent" />
                    Saving…
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {report ? "Update Report" : "Create Report"}
                  </>
                )}
              </button>
            </div>
            {/* Images (Cloudinary) */}
            <section className="rounded-2xl bg-white ring-1 ring-gray-200/70">
              <div className="border-b border-gray-200/70 px-4 py-3 sm:px-5">
                <div className="text-sm font-semibold text-gray-900">Images</div>
                <div className="text-xs text-gray-500">Upload report images (Cloudinary).</div>
              </div>

              <div className="p-4 sm:p-5">
                <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-5">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onPickFiles}
                    disabled={isLocked || uploading}
                    className="sr-only"
                    id="path-report-upload"
                  />
                  <label
                    htmlFor="path-report-upload"
                    className={`block cursor-pointer rounded-xl bg-white px-4 py-4 text-center shadow-sm ring-1 ring-gray-200/70 transition hover:bg-gray-50 focus-within:ring-2 focus-within:ring-[#235F72] ${
                      isLocked || uploading ? "cursor-not-allowed opacity-60" : ""
                    }`}
                  >
                    <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-[#235F72]/10 text-[#235F72]">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      {uploading ? "Uploading…" : "Upload images"}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">PNG/JPG up to 10MB</div>
                  </label>

                  {parsedImages.length > 0 ? (
                    <div className="mt-4">
                      <div className="text-xs font-semibold text-gray-700">Preview</div>
                      <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {parsedImages.map((src, idx) => (
                          <div key={src + idx} className="group overflow-hidden rounded-2xl ring-1 ring-gray-200/70 bg-white">
                            <a href={src} target="_blank" rel="noopener noreferrer" title="Open image">
                              <img src={src} alt={`img-${idx}`} className="h-28 w-full object-cover transition group-hover:scale-[1.02]" />
                            </a>
                            {!isLocked ? (
                              <button
                                type="button"
                                onClick={() => removeImageAt(idx)}
                                className="w-full border-t border-gray-100 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50"
                              >
                                Remove
                              </button>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </section>

            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default PathologistReportForm;
